import { FC, ReactElement, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Col, Row } from 'react-styled-flexboxgrid';
import { useDebounce, useMedia } from 'react-use';

import { BUTTON_TYPES, media } from '@savgroup-front-common/constants';
import { MessageType } from '@savgroup-front-common/types';

import { Button } from '../../../atoms/button';
import { Heading } from '../../../atoms/Heading';
import { SwipeableDrawer } from '../../../atoms/SwipeableDrawer';
import {
  DIRECTION,
  DRAWER_SIZE,
} from '../../../atoms/SwipeableDrawer/SwipeableDrawer';
import { SafeFormattedMessageWithoutSpread } from '../../../formatters';
import {
  DateRubric,
  DefaultRubric,
  Filters,
  RangeNumericRubric,
  Rubric,
} from '../Datatable.types';

import { useDatatableFilters } from './DatatableFilters.hooks';
import {
  $BadgeIconButton,
  $DatatableFilters,
  $DatatableFiltersFooter,
  $SwipeableOverflowContainer,
} from './DatatableFilters.styles';
import DatatableFiltersItem from './DatatableFiltersItem';
import DataTableSearchInput from './DataTableSearchInput/DataTableSearchInput';
import { SearchInputVariant } from './DataTableSearchInput/DataTableSearchInput.types';
import messages from './messages';

interface DatatableFiltersProps {
  filters?: Filters;
  selectedFilters?: {
    filtersState?: DefaultRubric[];
    numericFiltersState?: RangeNumericRubric[];
    dateFiltersState?: DateRubric[];
  };
  onSearchQueryChange?: (newValue: string) => void;
  onFilterChange?: (selectedValues: Rubric) => void;
  onFilterClear?: () => void;
  searchInputDefaultValue?: string;
  searchInputPlaceholder?: MessageType | string;
  postSearchInput?: ReactElement;
  componentThemeName?: string;
  dataTestId?: string;
  variant?: SearchInputVariant;
}

const DatatableFilters: FC<DatatableFiltersProps> = ({
  filters,
  onSearchQueryChange,
  onFilterChange,
  onFilterClear,
  selectedFilters,
  searchInputDefaultValue,
  searchInputPlaceholder,
  postSearchInput,
  componentThemeName,
  dataTestId,
  variant = SearchInputVariant.GLOBAL_SEARCH,
}) => {
  const isMobileView = useMedia(media.maxWidth.xs);
  const {
    isOpen,
    setOpen,
    formContext,
    selectedFiltersNumber,
    handleResetFilters,
  } = useDatatableFilters({
    filters,
    onFilterClear,
    selectedFilters,
  });
  const [localSearchQuery, setLocalSearchQuery] = useState(
    searchInputDefaultValue,
  );

  useDebounce(
    () => {
      if (onSearchQueryChange) {
        onSearchQueryChange(localSearchQuery || '');
      }
    },
    500,
    [localSearchQuery],
  );

  return (
    <$DatatableFilters>
      {onSearchQueryChange && (
        <>
          <DataTableSearchInput
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            placeholder={searchInputPlaceholder || messages.searchPlaceholder}
            searchInputDefaultValue={searchInputDefaultValue}
            variant={variant}
            componentThemeName={componentThemeName}
            dataTestId={`${dataTestId}_searchInput`}
          />
          {postSearchInput}
        </>
      )}

      {onFilterChange && (
        <Button
          tertiary
          type={BUTTON_TYPES.BUTTON}
          onClick={() => setOpen(true)}
          dataTestId={`${dataTestId}_filterButton`}
        >
          <SafeFormattedMessageWithoutSpread message={messages.filter} />
          {selectedFiltersNumber > 0 ? (
            <$BadgeIconButton>{selectedFiltersNumber}</$BadgeIconButton>
          ) : (
            <></>
          )}
        </Button>
      )}

      {onFilterChange && (
        <SwipeableDrawer
          isOpen={isOpen}
          onClose={() => setOpen(false)}
          direction={isMobileView ? DIRECTION.TOP : DIRECTION.LEFT}
          size={DRAWER_SIZE.TINY}
          hasBackdrop
        >
          <$SwipeableOverflowContainer>
            <Heading level={3}>
              <SafeFormattedMessageWithoutSpread
                message={messages.filterHeading}
              />
            </Heading>
            <FormProvider {...formContext}>
              <form
                onSubmit={(event) => {
                  event.preventDefault();

                  return undefined;
                }}
              >
                {filters?.map((filter) => {
                  return (
                    <DatatableFiltersItem
                      onChange={onFilterChange}
                      key={filter.name}
                      filter={filter}
                      dataTestId={`${dataTestId}_filtersItem`}
                    />
                  );
                })}
              </form>
            </FormProvider>
          </$SwipeableOverflowContainer>
          <$DatatableFiltersFooter>
            <Row>
              {selectedFiltersNumber === 0 ? (
                <Col xsOffset={5} xs={7}>
                  <Button
                    primary
                    fluid
                    type={BUTTON_TYPES.BUTTON}
                    onClick={() => setOpen(false)}
                    dataTestId={`${dataTestId}_filterButton_closePanel`}
                  >
                    <SafeFormattedMessageWithoutSpread
                      message={messages.closePanel}
                    />
                  </Button>
                </Col>
              ) : (
                <>
                  <Col xs={5}>
                    <Button
                      naked
                      primary
                      fluid
                      type={BUTTON_TYPES.BUTTON}
                      onClick={handleResetFilters}
                      dataTestId={`${dataTestId}_filterButton_resetFilters`}
                    >
                      <SafeFormattedMessageWithoutSpread
                        message={messages.resetFilters}
                      />
                    </Button>
                  </Col>
                  <Col xs={7}>
                    <Button
                      primary
                      fluid
                      type={BUTTON_TYPES.BUTTON}
                      onClick={() => setOpen(false)}
                      dataTestId={`${dataTestId}_filterButton_showResult`}
                    >
                      <SafeFormattedMessageWithoutSpread
                        message={messages.showResult}
                      />
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          </$DatatableFiltersFooter>
        </SwipeableDrawer>
      )}
    </$DatatableFilters>
  );
};

export default DatatableFilters;
